import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Pencil,
  Save,
  X,
  Lock,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Globe,
  Users,
  Clapperboard,
  Check,
  Maximize2,
  Wand2,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toast';

interface EpisodeItem {
  index: number;
  title?: string;
  narrative?: string;
  updatedAt?: string;
}

interface AgentStatus {
  status: 'idle' | 'running' | 'done' | 'failed';
  phase?: 'world' | 'characters' | 'episodes' | 'done';
  totalEpisodes?: number;
  completedEpisodes?: number[];
  missingEpisodes?: number[];
  error?: string | null;
}

interface StorylineStatusResponse {
  agentStatus: AgentStatus;
  storylinePlots?: EpisodeItem[];
  worldText?: string;
  charactersText?: string;
  missingEpisodes?: number[];
}

type GenerationMode = 'normal_hongguo' | 'premium';

interface Props {
  episodeId: string;
  audienceLocked: boolean;
  hasSelectedEpisodeCount: boolean;
  episodeCount: number;
  episodeDuration?: number;
  generationMode?: GenerationMode;
  worldText: string;
  charactersText: string;
  episodes: EpisodeItem[];
  onSaved: () => void;
  onJumpToAudience: () => void;
  onFullscreen?: () => void;
  locked?: boolean;
}

function SectionCard({
  icon,
  title,
  children,
  defaultOpen = false,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-outline-variant/30 bg-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <span className="text-brand-500">{icon}</span>
        <span className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
          {title}
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
        )}
      </button>
      {open && <div className="border-t border-outline-variant/20 px-3 pb-3 pt-2">{children}</div>}
    </div>
  );
}

function EditableTextBlock({
  value,
  patchPath,
  patchPayload,
  placeholder,
  rows = 8,
  locked = false,
  onSaved,
}: {
  value: string;
  patchPath: string;
  patchPayload: (text: string) => Record<string, unknown>;
  placeholder?: string;
  rows?: number;
  locked?: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const mut = useMutation({
    mutationFn: () =>
      api.patch(patchPath, { json: patchPayload(draft.trim()) }).json<unknown>(),
    onSuccess: () => {
      toast.success('已保存');
      onSaved();
      setEditing(false);
    },
    onError: (e: Error) => toast.error('保存失败', e.message),
  });

  if (!editing) {
    return (
      <div className="group relative">
        {value ? (
          <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-700">{value}</p>
        ) : (
          <p className="text-sm text-neutral-400">{placeholder || '暂无内容'}</p>
        )}
        {!locked && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="absolute right-0 top-0 flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-400 opacity-0 transition hover:bg-surface-variant hover:text-brand-600 group-hover:opacity-100"
          >
            <Pencil className="h-3 w-3" /> 编辑
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={rows}
        className="w-full resize-y rounded-md border border-brand-300 bg-surface px-2.5 py-1.5 text-sm leading-relaxed text-neutral-800 outline-none focus:ring-2 focus:ring-brand-200"
      />
      <div className="flex justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setDraft(value); setEditing(false); }}
          disabled={mut.isPending}
        >
          <X className="h-3.5 w-3.5" /> 取消
        </Button>
        <Button
          variant="agent"
          agentColor="writer"
          size="sm"
          onClick={() => mut.mutate()}
          disabled={mut.isPending || draft.trim() === value.trim()}
        >
          {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          保存
        </Button>
      </div>
    </div>
  );
}

function EpisodeRow({
  ep,
  episodeId,
  locked,
  onUpdated,
}: {
  ep: EpisodeItem;
  episodeId: string;
  locked: boolean;
  onUpdated: (updated: EpisodeItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(ep.title || '');
  const [draftNarrative, setDraftNarrative] = useState(ep.narrative || '');

  const genTitleMut = useMutation({
    mutationFn: () =>
      api
        .post(`writer/episodes/${episodeId}/episode-meta/${ep.index}/generate-title`)
        .json<{ title: string }>(),
    onSuccess: (data) => {
      setDraftTitle(data.title);
      toast.success('标题已生成', data.title);
    },
    onError: (e: Error) => toast.error('标题生成失败', e.message),
  });

  useEffect(() => {
    if (!editing) {
      setDraftTitle(ep.title || '');
      setDraftNarrative(ep.narrative || '');
    }
  }, [ep, editing]);

  const mut = useMutation({
    mutationFn: () =>
      api
        .patch(`writer/episodes/${episodeId}/episode-meta/${ep.index}`, {
          json: {
            title: draftTitle.trim() || undefined,
            narrative: draftNarrative.trim() || undefined,
          },
        })
        .json<unknown>(),
    onSuccess: () => {
      toast.success(`第 ${ep.index} 集已保存`);
      onUpdated({
        ...ep,
        title: draftTitle.trim() || ep.title,
        narrative: draftNarrative.trim() || ep.narrative,
        updatedAt: new Date().toISOString(),
      });
      setEditing(false);
    },
    onError: (e: Error) => toast.error('保存失败', e.message),
  });

  const title = ep.title || `第 ${ep.index} 集`;
  const hasDone = !!ep.narrative;

  return (
    <div className="rounded-md border border-outline-variant/20 bg-surface-variant/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span
          className={[
            'flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold',
            hasDone ? 'bg-brand-500 text-white' : ep.title ? 'bg-brand-100 text-brand-700' : 'bg-neutral-100 text-neutral-400',
          ].join(' ')}
        >
          {ep.index}
        </span>
        <span className="flex-1 truncate text-xs font-medium text-neutral-700">{title}</span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
        )}
      </button>

      {open && (
        <div className="border-t border-outline-variant/15 px-3 pb-3 pt-2">
          {editing ? (
            <div className="space-y-2">
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wide text-neutral-500">标题</label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    className="flex-1 rounded border border-brand-300 bg-surface px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-brand-300"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => genTitleMut.mutate()}
                    disabled={genTitleMut.isPending}
                    title="AI 生成标题"
                    className="shrink-0 px-2"
                  >
                    {genTitleMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wide text-neutral-500">剧情叙述</label>
                <textarea
                  value={draftNarrative}
                  onChange={(e) => setDraftNarrative(e.target.value)}
                  rows={8}
                  className="w-full resize-y rounded border border-brand-300 bg-surface px-2 py-1.5 text-sm leading-relaxed outline-none focus:ring-1 focus:ring-brand-300"
                />
              </div>
              <div className="flex justify-end gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={mut.isPending}>
                  <X className="h-3.5 w-3.5" /> 取消
                </Button>
                <Button variant="agent" agentColor="writer" size="sm" onClick={() => mut.mutate()} disabled={mut.isPending}>
                  {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  保存
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-neutral-600">{title}</span>
                {!locked && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-400 transition hover:bg-surface-variant hover:text-brand-600"
                  >
                    <Pencil className="h-3 w-3" /> 编辑
                  </button>
                )}
              </div>
              {ep.narrative && (
                <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-700">{ep.narrative}</p>
              )}
              {!ep.narrative && (
                <p className="text-xs text-neutral-400">暂无内容 · 生成后自动显示</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function phaseLabel(s: AgentStatus, episodeCount: number): string {
  if (s.phase === 'world') return '世界观生成中…';
  if (s.phase === 'characters') return '出场人物生成中…';
  if (s.phase === 'episodes') {
    const done = s.completedEpisodes?.length ?? 0;
    const total = s.totalEpisodes ?? episodeCount;
    return `逐集剧情生成中… ${done}/${total} 集完成`;
  }
  return '生成中…';
}

export function StorylineEditor({
  episodeId,
  audienceLocked,
  hasSelectedEpisodeCount,
  episodeCount,
  episodeDuration = 3,
  generationMode = 'normal_hongguo',
  worldText,
  charactersText,
  episodes,
  onSaved,
  onJumpToAudience,
  onFullscreen,
  locked = false,
}: Props) {
  const [selectedMode, setSelectedMode] = useState<GenerationMode>(generationMode);
  const [modeSaving, setModeSaving] = useState(false);
  const [agentRunning, setAgentRunning] = useState(false);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [prompt, setPrompt] = useState('');
  const [liveEpisodes, setLiveEpisodes] = useState<EpisodeItem[]>([]);
  const [liveWorldText, setLiveWorldText] = useState('');
  const [liveCharactersText, setLiveCharactersText] = useState('');
  const [cleared, setCleared] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setSelectedMode(generationMode);
  }, [episodeId, generationMode]);

  const saveGenerationMode = async (nextMode: GenerationMode) => {
    if (agentRunning || modeSaving || nextMode === selectedMode) return;
    const prev = selectedMode;
    setSelectedMode(nextMode);
    setModeSaving(true);
    try {
      await api
        .patch(`writer/episodes/${episodeId}/mode`, {
          json: { mode: nextMode },
        })
        .json<{ mode: string | null; updatedAt: string }>();
      onSaved();
    } catch (e) {
      setSelectedMode(prev);
      toast.error('生成方式保存失败', (e as Error).message || '请稍后重试');
    } finally {
      setModeSaving(false);
    }
  };

  const displayWorldText = cleared ? liveWorldText : (liveWorldText || worldText);
  const displayCharactersText = cleared ? liveCharactersText : (liveCharactersText || charactersText);
  const mergedEpisodes: EpisodeItem[] = (() => {
    if (cleared) return [...liveEpisodes].sort((a, b) => a.index - b.index);
    const base = new Map(episodes.map((e) => [e.index, e]));
    for (const e of liveEpisodes) base.set(e.index, e);
    return Array.from(base.values()).sort((a, b) => a.index - b.index);
  })();

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPoll = () => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api
          .get(`writer/episodes/${episodeId}/full-storyline/agent-status`)
          .json<StorylineStatusResponse>();
        const nextStatus: AgentStatus = {
          ...res.agentStatus,
          missingEpisodes: res.agentStatus.missingEpisodes ?? res.missingEpisodes,
        };
        setAgentStatus(nextStatus);
        if (res.storylinePlots?.length) setLiveEpisodes(res.storylinePlots);
        if (res.worldText) setLiveWorldText(res.worldText);
        if (res.charactersText) setLiveCharactersText(res.charactersText);
        if (nextStatus.status === 'done') {
          stopPoll();
          setAgentRunning(false);
          setCleared(false);
          onSaved();
          toast.success('剧情脚本生成完成');
        } else if (nextStatus.status === 'failed') {
          stopPoll();
          setAgentRunning(false);
          toast.error('生成失败', nextStatus.error || '请重跑失败集');
        }
      } catch {
        stopPoll();
        setAgentRunning(false);
      }
    }, 3000);
  };

  const startAgent = async (options?: { reset?: boolean }) => {
    const reset = options?.reset ?? true;
    const p = prompt.trim();
    setAgentRunning(true);
    setAgentStatus({
      status: 'running',
      phase: reset ? 'world' : 'episodes',
      totalEpisodes: episodeCount,
      completedEpisodes: sortedEpisodes.filter((e) => !!e.narrative).map((e) => e.index),
      missingEpisodes: reset ? [] : missingEpisodeIndexes,
    });
    if (reset) {
      setCleared(true);
      setLiveWorldText('');
      setLiveCharactersText('');
      setLiveEpisodes([]);
    }
    try {
      await api.post(`writer/episodes/${episodeId}/full-storyline/agent-start`, {
        json: {
          episodeCount,
          episodeDuration,
          mode: selectedMode,
          prompt: p || undefined,
          reset,
        },
      }).json();
      if (!reset) toast.info('已开始重跑失败集', `将补齐第 ${missingEpisodeIndexes.slice(0, 8).join('、')}${missingEpisodeIndexes.length > 8 ? '…' : ''} 集`);
      startPoll();
    } catch (e) {
      setAgentRunning(false);
      setAgentStatus(null);
      toast.error('启动失败', (e as Error).message || '请重试');
    }
  };

  useEffect(() => {
    api
      .get(`writer/episodes/${episodeId}/full-storyline/agent-status`)
      .json<StorylineStatusResponse>()
      .then((res) => {
        const nextStatus: AgentStatus = {
          ...res.agentStatus,
          missingEpisodes: res.agentStatus.missingEpisodes ?? res.missingEpisodes,
        };
        if (res.storylinePlots?.length) setLiveEpisodes(res.storylinePlots);
        if (res.worldText) setLiveWorldText(res.worldText);
        if (res.charactersText) setLiveCharactersText(res.charactersText);
        if (nextStatus.status === 'running') {
          setAgentRunning(true);
          setAgentStatus(nextStatus);
          startPoll();
        } else if (nextStatus.status === 'failed') {
          setAgentStatus(nextStatus);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeId]);

  useEffect(() => () => stopPoll(), []);

  const sortedEpisodes = mergedEpisodes;
  const completedCount = sortedEpisodes.filter((e) => !!e.narrative).length;
  const computedMissingEpisodeIndexes = Array.from({ length: episodeCount }, (_, i) => i + 1).filter(
    (idx) => !sortedEpisodes.some((e) => e.index === idx && !!e.narrative),
  );
  const statusMissingEpisodeIndexes = (agentStatus?.missingEpisodes ?? []).filter(
    (idx) => Number.isInteger(idx)
      && idx >= 1
      && idx <= episodeCount
      && !sortedEpisodes.some((e) => e.index === idx && !!e.narrative),
  );
  const missingEpisodeIndexes = statusMissingEpisodeIndexes.length > 0
    ? statusMissingEpisodeIndexes
    : computedMissingEpisodeIndexes;

  const [episodesOpen, setEpisodesOpen] = useState(true);

  const handleEpisodeUpdated = (updated: EpisodeItem) => {
    setLiveEpisodes((prev) => {
      const map = new Map(prev.map((e) => [e.index, e]));
      map.set(updated.index, updated);
      return Array.from(map.values()).sort((a, b) => a.index - b.index);
    });
  };

  const hasAnyContent = displayWorldText || displayCharactersText || sortedEpisodes.length > 0;
  const canRetryMissing = !locked
    && !agentRunning
    && completedCount > 0
    && missingEpisodeIndexes.length > 0
    && hasAnyContent;

  if (!audienceLocked) {
    return (
      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-wider text-outline">剧情脚本</div>
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-outline-variant/40 bg-surface-variant/30 px-4 py-6 text-center">
          <Lock className="h-5 w-5 text-outline" />
          <p className="text-sm text-neutral-600">请先锁定目标用户人群画像，再生成剧情脚本</p>
          <Button variant="secondary" size="sm" onClick={onJumpToAudience}>
            <Lock className="h-3.5 w-3.5" /> 去锁定画像
          </Button>
        </div>
      </div>
    );
  }

  if (!hasSelectedEpisodeCount) {
    return (
      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-wider text-outline">剧情脚本</div>
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-warning/40 bg-warning-soft/20 px-4 py-6 text-center">
          <AlertTriangle className="h-5 w-5 text-warning" />
          <p className="text-sm text-neutral-700">请先在上方「剧情集数」中选择集数</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="shrink-0 text-xs uppercase tracking-wider text-outline">剧情脚本</div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          {sortedEpisodes.length > 0 && (
            <span className="text-[11px] text-neutral-500">
              {completedCount}/{episodeCount} 集
            </span>
          )}
          {!locked && !agentRunning && (
            <Button variant="agent" agentColor="writer" size="sm" onClick={() => startAgent()}>
              <Wand2 className="h-3.5 w-3.5" />
              {hasAnyContent ? 'AI 重新生成' : 'AI 生成'}
            </Button>
          )}
          {canRetryMissing && (
            <Button variant="secondary" size="sm" onClick={() => startAgent({ reset: false })}>
              <RotateCcw className="h-3.5 w-3.5" />
              重跑失败 {missingEpisodeIndexes.length} 集
            </Button>
          )}
          {agentRunning && (
            <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-brand-600">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span className="min-w-0 break-words">
                {agentStatus ? phaseLabel(agentStatus, episodeCount) : '生成中…'}
              </span>
            </span>
          )}
          {onFullscreen && (
            <button
              type="button"
              onClick={onFullscreen}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-400 transition hover:bg-surface-variant hover:text-brand-600"
              title="全屏编辑"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-outline-variant/30 bg-surface-variant/20 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              生成方式
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">
              {selectedMode === 'premium'
                ? '精品：独立精品节奏，补强空间、道具、动作结果、情绪留白和尾帧钩子。'
                : '普通（红果）：强钩子、快节奏、台词驱动，按红果式爆点和节奏校验生成。'}
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-outline-variant/30 bg-white/70 p-0.5">
            <button
              type="button"
              onClick={() => saveGenerationMode('normal_hongguo')}
              disabled={locked || agentRunning || modeSaving}
              className={[
                'rounded-full px-2.5 py-1 text-[11px] transition disabled:cursor-not-allowed disabled:opacity-60',
                selectedMode === 'normal_hongguo'
                  ? 'bg-brand-500 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-brand-600',
              ].join(' ')}
            >
              普通（红果）
            </button>
            <button
              type="button"
              onClick={() => saveGenerationMode('premium')}
              disabled={locked || agentRunning || modeSaving}
              className={[
                'rounded-full px-2.5 py-1 text-[11px] transition disabled:cursor-not-allowed disabled:opacity-60',
                selectedMode === 'premium'
                  ? 'bg-brand-500 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-brand-600',
              ].join(' ')}
            >
              精品
            </button>
          </div>
        </div>
      </div>

      {agentRunning && agentStatus?.phase === 'episodes' && (
        <div className="space-y-1.5 rounded-lg border border-brand-200 bg-brand-50/40 px-3 py-2">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-brand-100">
            <div
              className="h-full bg-brand-500 transition-all duration-500"
              style={{
                width: `${Math.round(((agentStatus.completedEpisodes?.length ?? 0) / (agentStatus.totalEpisodes ?? episodeCount)) * 100)}%`,
              }}
            />
          </div>
          <p className="text-[11px] text-neutral-400">生成完成后自动展示，期间可继续其他操作</p>
        </div>
      )}

      {agentStatus?.status === 'failed' && !agentRunning && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-danger/30 bg-danger-soft/20 px-3 py-2 text-xs">
          <div className="min-w-0">
            <p className="font-medium text-danger">上次生成失败</p>
            <p className="mt-0.5 text-neutral-600">{agentStatus.error || '请重新生成'}</p>
          </div>
          {canRetryMissing && (
            <Button variant="secondary" size="sm" onClick={() => startAgent({ reset: false })}>
              <RotateCcw className="h-3.5 w-3.5" />
              重跑失败集
            </Button>
          )}
        </div>
      )}

      {!hasAnyContent && !locked && !agentRunning && (
        <div
          onClick={() => startAgent()}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-brand-300/60 bg-brand-50/30 px-4 py-6 text-center transition hover:border-brand-500 hover:bg-brand-50/60"
        >
          <Wand2 className="h-5 w-5 text-brand-600" />
          <p className="text-sm text-brand-700">点击一键生成「剧情脚本」</p>
          <p className="text-xs text-neutral-500">
            先生成世界观与人物，再并发补全每集剧情（每集独立可手动修改）
          </p>
        </div>
      )}

      {!locked && !agentRunning && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') startAgent(); }}
              placeholder="可选补充要求：如「加强反转节奏」「主角是法医女博士」…"
              className="min-w-0 flex-1 rounded-md border border-outline-variant/40 bg-surface px-2.5 py-1.5 text-xs text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none"
            />
          </div>
        </div>
      )}

      {displayWorldText && (
        <SectionCard icon={<Globe className="h-3.5 w-3.5" />} title="世界观背景" defaultOpen={false}>
          <EditableTextBlock
            value={displayWorldText}
            patchPath={`writer/episodes/${episodeId}/storyline/sections`}
            patchPayload={(text) => ({ section: 'world', text })}
            placeholder="世界观尚未生成"
            rows={8}
            locked={locked}
            onSaved={onSaved}
          />
        </SectionCard>
      )}

      {displayCharactersText && (
        <SectionCard icon={<Users className="h-3.5 w-3.5" />} title="出场人物" defaultOpen={false}>
          <EditableTextBlock
            value={displayCharactersText}
            patchPath={`writer/episodes/${episodeId}/storyline/sections`}
            patchPayload={(text) => ({ section: 'characters', text })}
            placeholder="人物设定尚未生成"
            rows={10}
            locked={locked}
            onSaved={onSaved}
          />
        </SectionCard>
      )}

      {sortedEpisodes.length > 0 && (
        <div className="rounded-lg border border-outline-variant/30 bg-surface">
          <button
            type="button"
            onClick={() => setEpisodesOpen((v) => !v)}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
          >
            <span className="text-brand-500">
              <Clapperboard className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
              逐集剧情
            </span>
            <span className="text-[10px] text-neutral-400">
              {completedCount}/{episodeCount} 集
            </span>
            {episodesOpen ? (
              <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
            )}
          </button>
          {episodesOpen && (
            <div className="max-h-[60vh] space-y-1 overflow-y-auto border-t border-outline-variant/20 p-2">
              {sortedEpisodes.map((ep) => (
                <EpisodeRow
                  key={ep.index}
                  ep={ep}
                  episodeId={episodeId}
                  locked={locked}
                  onUpdated={handleEpisodeUpdated}
                />
              ))}
              {sortedEpisodes.length < episodeCount && (
                <div className="grid grid-cols-10 gap-1 pt-1">
                  {Array.from({ length: episodeCount }, (_, i) => i + 1)
                    .filter((i) => !sortedEpisodes.find((e) => e.index === i))
                    .map((i) => (
                      <div
                        key={i}
                        className="flex h-6 items-center justify-center rounded bg-neutral-100 text-[10px] text-neutral-400"
                      >
                        {i}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
